/**
 * Sanitizes page snapshots before sending to API.
 * Strips PII, user data, and sensitive content.
 * Preserves product structure (navigation text, labels, headings).
 */

import type { PageSnapshot } from './element-mapper.js';

const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const PHONE_REGEX = /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;
const SSN_REGEX = /\d{3}-\d{2}-\d{4}/g;
const CC_REGEX = /\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}/g;

/**
 * Sanitize a snapshot, stripping PII while preserving structure.
 */
export function sanitizeSnapshot(snapshot: PageSnapshot): PageSnapshot {
  return {
    ...snapshot,
    urlPath: sanitizeUrl(snapshot.urlPath),
    headings: snapshot.headings,
    elements: snapshot.elements.map((el) => ({
      ...el,
      label: sanitizeText(el.label),
      containerLabel: el.containerLabel ? sanitizeText(el.containerLabel) : undefined,
      options: el.options?.map((opt) => ({
        value: opt.value,
        label: sanitizeText(opt.label),
      })),
    })),
    navigation: snapshot.navigation.map((n) => ({
      text: n.text,
      href: sanitizeUrl(n.href),
    })),
    forms: snapshot.forms.map((f) => ({
      ...f,
      fields: f.fields.map((field) => ({
        ...field,
        label: sanitizeText(field.label),
        options: field.options?.map((opt) => ({
          value: opt.value,
          label: sanitizeText(opt.label),
        })),
      })),
    })),
    tables: snapshot.tables.map((t) => ({
      headers: t.headers,
      rowCount: t.rowCount,
    })),
    cards: (snapshot.cards ?? []).map((c) => ({
      ...c,
      label: sanitizeText(c.label),
    })),
    popovers: (snapshot.popovers ?? []).map((p) => ({
      ...p,
      content: sanitizeText(p.content),
    })),
    fieldGroups: (snapshot.fieldGroups ?? []).map((g) => ({
      ...g,
      label: sanitizeText(g.label),
      options: g.options.map((opt) => ({
        ...opt,
        label: sanitizeText(opt.label),
      })),
    })),
  };
}

function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url, location.origin);
    const safeParams = ['page', 'tab', 'view', 'sort', 'filter'];
    const cleanParams = new URLSearchParams();
    for (const [key, value] of parsed.searchParams) {
      if (safeParams.includes(key)) {
        cleanParams.set(key, value);
      }
    }
    return parsed.pathname + (cleanParams.toString() ? `?${cleanParams}` : '');
  } catch {
    return url.split('?')[0];
  }
}

function sanitizeText(text: string): string {
  return text
    .replace(EMAIL_REGEX, '[email]')
    .replace(PHONE_REGEX, '[phone]')
    .replace(SSN_REGEX, '[ssn]')
    .replace(CC_REGEX, '[card]');
}
